import { ICONES } from "./visual.jsx";

/*
 * O que a paleta FAZ — e não só onde ela leva.
 *
 * Ela achava cinco coisas e não fazia nenhuma: era índice, não paleta de
 * comandos. Quem queria abrir uma tarefa precisava saber que ela mora em
 * `/tarefas` e que o botão fica no canto do cabeçalho — três passos para o que
 * cabe em três teclas.
 *
 * `palavras` existe porque ninguém digita o rótulo: digita o verbo ("criar",
 * "abrir"), o objeto ("cliente", "prazo") ou o sinônimo do escritório ("lead").
 * `pode` é a MESMA regra dos outros grupos — verbo que responde 403 é beco com
 * atalho de teclado.
 *
 * `soBuscando`: verbo que só aparece depois de digitar. Os modos da IA são três
 * linhas que quem configura usa uma vez por mês — na paleta aberta, antes de
 * qualquer tecla, eles empurrariam para baixo o que se faz todo dia.
 *
 * Verbo novo entra aqui e em nenhum outro lugar. Só entra o que tem um caminho
 * de verdade: "nova resposta pronta" ficou de fora de propósito, porque o
 * formulário dela é estado local da vista (`Rapidas.jsx` diz o porquê: é
 * formulário, não lugar) e a paleta não tem endereço para abrir.
 *
 * Mora fora de `Paleta.jsx` porque a regra de quem vê cada verbo se prende em
 * teste (`paleta.test.jsx`), e o componente puxa o registro inteiro do shadcn.
 */
export const VERBOS = [
  {
    chave: "novo-atendimento",
    rotulo: "Novo atendimento",
    palavras: "criar novo atendimento cliente caso lead cadastrar abrir",
    icone: ICONES.chats,
    pode: "registros:criar",
    nota: "pergunta nome e telefone",
  },
  {
    chave: "puxar-fila",
    rotulo: "Puxar o próximo da fila",
    palavras: "puxar proximo fila pegar atender sem responsavel",
    icone: ICONES.avancar,
    pode: "leads:ver",
    nota: "abre quem está esperando",
  },
  {
    chave: "nova-tarefa",
    rotulo: "Nova tarefa",
    palavras: "criar nova tarefa prazo lembrete agenda compromisso",
    icone: ICONES.tarefas,
    pode: "registros:editar",
    caminho: "/tarefas?nova=1",
  },
  {
    chave: "nova-pessoa",
    rotulo: "Cadastrar pessoa na equipe",
    palavras: "criar cadastrar pessoa equipe usuario acesso advogado convidar",
    icone: ICONES.equipe,
    pode: "equipe:editar",
    caminho: "/equipe?pessoa=nova",
  },
  {
    /* Etiquetar pela paleta porque a etiqueta é o recorte que o escritório
       inventa e a paleta é onde se digita o que se quer: "urgente" pensado em
       voz alta, sem procurar em que cartão da coluna ele mora. Só aparece com um
       caso ABERTO — é nele que a etiqueta vai. */
    chave: "etiquetar",
    rotulo: "Etiquetar este atendimento",
    palavras: "etiqueta etiquetar marcar urgente rotulo tag recorte",
    icone: ICONES.etiqueta,
    pode: "registros:editar",
    exigeCaso: true,
    nota: "põe, tira e organiza",
  },
  {
    chave: "comecar",
    rotulo: "Primeiros passos",
    palavras: "primeiros passos comecar começar inicio boas vindas configurar setor onboarding o que falta",
    icone: ICONES.brilho,
    pode: "registros:ver",
    caminho: "/comecar",
    nota: "o que falta para você",
  },
  {
    chave: "demonstracao",
    rotulo: "Ver a demonstração da mesa",
    palavras: "demonstracao demo tour passeio aprender como funciona ajuda",
    icone: ICONES.ajuda,
    pode: "leads:ver",
    nota: "a mesa passeia sozinha, sem enviar nada",
  },
  /* Os três modos: mudar o modo do setor é `config:editar`, a mesma porta do
     cartão de Ajustes. Mudar o de UM caso é no cabeçalho da conversa. */
  ...[
    ["sem_ia", "Sem IA", "sem ia manual regra fixa desligar ia"],
    ["copiloto", "Copiloto", "copiloto sugere prepara"],
    ["piloto", "Piloto automático", "piloto automatico agentes sozinha ligar ia"],
  ].map(([modo, nome, extra]) => ({
    chave: `modo-${modo}`,
    modo,
    rotulo: `Modo da IA: ${nome}`,
    palavras: `modo ia quanto a ia faz setor ${extra}`,
    icone: ICONES.ia,
    pode: "config:editar",
    soBuscando: true,
    nota: "vale para o setor inteiro",
  })),
];

/** Casa "angelica" com "ANGÉLICA": acento não pode esconder cliente. */
export const cru = (texto) =>
  (texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Os verbos que ESTA pessoa alcança, agora, para este termo.
 *
 * Os setores entram como verbo (um por setor além do atual): trocar de setor é
 * a ação mais perigosa de esquecer — gravar no setor errado — e a paleta é onde
 * se digita "previdenciário" sem saber que o seletor mora ao lado da marca.
 */
export function verbosDaPaleta({ pode, casoId = null, termo = "", workspace = null, workspaces = [] }) {
  const busca = cru(termo.trim());
  const setores = workspaces
    .filter((w) => w.chave !== workspace?.chave)
    .map((w) => ({
      chave: `setor-${w.chave}`,
      setor: w.chave,
      rotulo: `Ir para o setor ${w.nome}`,
      palavras: `trocar mudar setor escritorio ${w.nome}`,
      icone: ICONES.pasta,
      nota: "a mesa recarrega lá",
    }));
  return [...VERBOS, ...setores].filter(
    (v) =>
      (!v.pode || pode(v.pode)) &&
      /* Verbo que age sobre O CASO ABERTO não existe sem caso aberto: ele
         apareceria na lista e não teria em quem agir. */
      (!v.exigeCaso || Boolean(casoId)) &&
      (!v.soBuscando || busca.length > 0) &&
      (!busca || cru(`${v.rotulo} ${v.palavras}`).includes(busca)),
  );
}
